import React, { useEffect, useRef } from "react";
import Image from "next/image";
import { lightenColor } from "@/utils/lightenColor";
import { useTranslations } from "next-intl";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { Employee } from "@/dataLayer/getEmployeesForService";
import { ImageType } from "@/dto/image";
import { Item } from "@/dto/item";
import { useCurrency } from "@/app/providers/currencyContext";

export const ServiceModalEmployeeSelection = ({
  productImage,
  service,
  employeeList,
  handleEmployeeSelect,
  selectedEmployee,
  setShowEmployeeSelection,
  handleAddToServiceCart,
  finalPrice,
  merchantColor,
  imageRef,
  scrollContainerRef,
  titleId,
}: {
  productImage: ImageType | null;
  service: Item | null;
  employeeList: Employee[];
  // eslint-disable-next-line no-unused-vars
  handleEmployeeSelect: (employeeUuid: string) => void;
  selectedEmployee: string;
  setShowEmployeeSelection: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddToServiceCart: () => void;
  finalPrice: number;
  merchantColor: string;
  imageRef?: any;
  scrollContainerRef?: any;
  titleId?: string;
}) => {
  const t = useTranslations("ServiceModalEmployeeSelection");
  const { format } = useCurrency();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const radioColor: React.CSSProperties = {
    "--checked-bg-color": merchantColor,
    "--checked-border-color": merchantColor,
  } as React.CSSProperties;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (headingRef.current) {
        headingRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      ref={scrollContainerRef}
      className="flex flex-col sm:flex-row-reverse w-full h-full overflow-y-auto sm:overflow-y-hidden"
    >
      {productImage && (
        <div className="flex-shrink-0 sm:w-[50%] sm:max-h-full">
          <Image
            aria-hidden={true}
            ref={imageRef}
            width={450}
            height={576}
            alt={service?.name ?? t("serviceNameAlt")}
            src={productImage.source}
            className="object-cover object-center w-auto h-full"
          />
        </div>
      )}
      <div
        className={`flex flex-col sm:min-h-[260px] w-full ${
          productImage ? "sm:w-[50%]" : "sm:w-[530px] pb-[64px]"
        } relative h-auto`}
      >
        <div className="px-6 pt-6 bg-white sticky top-0 shadow-modalStickyHeader">
          <h2
            id={titleId}
            ref={headingRef}
            tabIndex={-1}
            className="sm:pt-4 text-4xl text-[32px] font-medium text-[#373737] whitespace-nowrap overflow-hidden text-ellipsis"
          >
            {t("selectProfessional")}
          </h2>
        </div>
        <div className="py-4 px-6 sm:overflow-y-auto pb-36 sm:pb-0">
          <p className="text-[15px]  text-[#727272] font-semibold">
            {t("staff")}
          </p>
          <div className="my-4 pr-4 sm:max-h-[350px]">
            {employeeList.map((employee: any) => (
              <label
                key={employee.employeeUuid}
                className="flex w-full items-center mb-4 border-b border-[#E5E5E5] pb-4 cursor-pointer"
                style={radioColor}
              >
                <input
                  type="radio"
                  name="employee"
                  value={employee.employeeUuid}
                  checked={selectedEmployee === employee.employeeUuid}
                  aria-label={employee.nickName || employee.name}
                  onChange={() => handleEmployeeSelect(employee.employeeUuid)}
                  className="mr-3 appearance-none w-[22px] h-[22px] border border-gray-400 rounded-full checked:bg-[var(--checked-bg-color)] checked:border-[var(--checked-border-color)] flex items-center justify-center checked:after:content-[''] checked:after:bg-white checked:after:w-[10px] checked:after:h-[10px] checked:after:rounded-full"
                  data-testid={applyDataTestId(pom.scheduler.avatar)}
                />
                <div
                  aria-hidden={true}
                  className="w-10 h-10 bg-[#C4C4C4] rounded-full flex items-center justify-center mr-4 font-light text-[20px]"
                >
                  {employee?.name?.[0]}
                </div>
                <div className="flex-1 text-left" aria-hidden={true}>
                  <p className="font-normal text-[15px]">
                    {employee.nickName || employee.name}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="fixed sm:absolute bottom-0 left-0 w-full h-32 sm:h-[64px] flex flex-col sm:flex-row">
          <button
            style={{
              backgroundColor: lightenColor(merchantColor, 90),
            }}
            className="w-full sm:w-2/5 h-full bg-[#E5E5E5] text-gray-900 text-xl font-medium flex items-center justify-center"
            onClick={() => setShowEmployeeSelection(false)}
          >
            {t("back")}
          </button>

          <button
            style={{
              backgroundColor: merchantColor,
            }}
            className="w-full sm:w-3/5 h-full bg-[#2E2E2E] text-white text-xl font-medium clover-button-primary flex flex-row items-center justify-between px-4 disabled:bg-[#E5E5E5]"
            onClick={() => {
              handleAddToServiceCart();
            }}
            data-testid={applyDataTestId(pom.serviceModal.bookButton)}
          >
            <span>{t("book")}</span>
            <span> {format(finalPrice)}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
